import { FC, createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useLocalStorage } from '@mantine/hooks';

type ColorSchemePreference = 'light' | 'dark' | 'auto';
type ResolvedColorScheme = 'light' | 'dark';

interface ThemeContextType {
    colorScheme: ResolvedColorScheme;
    colorSchemePreference: ColorSchemePreference;
    toggleColorScheme: () => void;
    setColorScheme: (scheme: ColorSchemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
};

const getSystemColorScheme = (): ResolvedColorScheme => {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
};

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
    const [colorSchemePreference, setColorSchemePreference] = useLocalStorage<ColorSchemePreference>({
        key: 'mantine-color-scheme',
        defaultValue: 'auto',
    });

    const [systemColorScheme, setSystemColorScheme] = useState<ResolvedColorScheme>(getSystemColorScheme);

    // Update system color scheme when switching to auto
    useEffect(() => {
        if (colorSchemePreference === 'auto') {
            setSystemColorScheme(getSystemColorScheme());
        }
    }, [colorSchemePreference]);

    // Listen for system theme changes
    useEffect(() => {
        if (colorSchemePreference !== 'auto') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
            setSystemColorScheme(e.matches ? 'dark' : 'light');
        };

        // Set initial value
        handleChange(mediaQuery);

        // Modern browsers
        if (mediaQuery.addEventListener) {
            const changeHandler = (e: MediaQueryListEvent) => handleChange(e);
            mediaQuery.addEventListener('change', changeHandler);
            return () => mediaQuery.removeEventListener('change', changeHandler);
        } 
        // Fallback for older browsers
        else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, [colorSchemePreference]);

    // Resolve the actual color scheme based on preference
    const resolvedColorScheme: ResolvedColorScheme = 
        colorSchemePreference === 'auto' ? systemColorScheme : colorSchemePreference;

    const toggleColorScheme = () => {
        if (colorSchemePreference === 'light') {
            setColorSchemePreference('dark');
        } else if (colorSchemePreference === 'dark') {
            setColorSchemePreference('auto');
        } else {
            setColorSchemePreference('light');
        }
    };

    return (
        <ThemeContext.Provider value={{ 
            colorScheme: resolvedColorScheme, 
            colorSchemePreference,
            toggleColorScheme, 
            setColorScheme: setColorSchemePreference 
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

